import React, { useState } from "react";
import { Elements, CardNumberElement, CardExpiryElement, CardCvcElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { getStripe } from "../common/stripeClient";
import { PrimaryButton } from "../artistStudio/shared";
import { soundApi, errorCode, errorText, money } from "./soundApi";

// Pay for a licence by card (Stripe). The server opens the payment for the price it checked itself; the licence is given only when
// the server has read the payment from Stripe as succeeded (and also by Stripe's webhook, so closing the page after paying is safe).
// Used inside the licence dialog's checkout step; the agreements and the billing address are handled by the dialog and passed in.

const ELEMENT_OPTIONS = {
  style: {
    base: { fontSize: "16px", color: "#374151", "::placeholder": { color: "#9ca3af" }, fontFamily: "inherit" },
    invalid: { color: "#BE1F2E" },
  },
};

// The codes that mean "look at the options again": the price moved, the licence is gone or you already have it.
export const RELOAD_CODES = ["PRICE_CHANGED", "LICENCE_UNAVAILABLE", "LICENSING_DISABLED", "ALREADY_LICENSED"];

function CardFields({ soundId, summary, billing, autoRenew, canPay, disabled, onPaid, onProblem, onFailure }) {
  const stripe = useStripe();
  const elements = useElements();
  const [name, setName] = useState("");
  const [nameError, setNameError] = useState("");
  const [busy, setBusy] = useState(false);

  const pay = async () => {
    if (!stripe || !elements) { onFailure("The payment form is still loading. Please try again in a moment."); return; }
    if (!name.trim()) { setNameError("Enter the name on the card."); return; }
    setNameError("");
    setBusy(true);
    onFailure("");
    try {
      let started;
      try {
        started = await soundApi.checkoutStart({
          track_id: soundId, usage_class: summary.usage_class, term: summary.term, expected_price: summary.price, accepted_terms: true,
          auto_renew: Boolean(autoRenew), ...(billing ? { billing_address: billing } : {}),
        });
      } catch (e) {
        if (RELOAD_CODES.includes(errorCode(e))) onProblem(e);
        else onFailure(errorText(e, "The payment could not be started. Please try again."));
        return;
      }
      if (started.payment_status === "paid") { onPaid(started.licence); return; }
      const { error } = await stripe.confirmCardPayment(started.client_secret, {
        payment_method: { card: elements.getElement(CardNumberElement), billing_details: { name: name.trim() } },
      });
      // Whatever the card result, the server decides: it reads the payment from Stripe before giving anything.
      try {
        const done = await soundApi.checkoutConfirm({ track_id: soundId, payment_intent_id: started.payment_intent_id });
        if (done && done.status === "pending") onFailure(done.message || "Your payment is still being processed. Your licence will appear in My Licences when it is confirmed.");
        else onPaid(done.licence);
      } catch (e) {
        if (RELOAD_CODES.includes(errorCode(e))) onProblem(e);
        else onFailure(error ? `${error.message} No licence was given and you have not been charged.` : errorText(e, "We could not confirm your payment. Please check My Licences before trying again."));
      }
    } finally {
      setBusy(false);
    }
  };

  const off = busy || disabled || !canPay || !stripe;
  return (
    <div className="mt-4" data-testid="licence-card-form">
      <label className="block text-sm text-gray-600 mb-1" htmlFor="licence-card-name">Name on card</label>
      <input
        id="licence-card-name" type="text" autoComplete="cc-name" value={name} onChange={(e) => setName(e.target.value)} disabled={busy}
        className="w-full rounded-lg border border-gray-300 px-3 py-3 text-base" data-testid="licence-card-name"
      />
      {nameError && <p className="mt-1 text-xs text-red-700" role="alert">{nameError}</p>}
      <label className="block text-sm text-gray-600 mt-3 mb-1">Card number</label>
      <div className="border border-gray-300 rounded-lg px-3 py-3 bg-white"><CardNumberElement options={ELEMENT_OPTIONS} /></div>
      <div className="grid grid-cols-2 gap-3 mt-3">
        <div>
          <label className="block text-sm text-gray-600 mb-1">Expiry date</label>
          <div className="border border-gray-300 rounded-lg px-3 py-3 bg-white"><CardExpiryElement options={ELEMENT_OPTIONS} /></div>
        </div>
        <div>
          <label className="block text-sm text-gray-600 mb-1">CVC</label>
          <div className="border border-gray-300 rounded-lg px-3 py-3 bg-white"><CardCvcElement options={ELEMENT_OPTIONS} /></div>
        </div>
      </div>
      <PrimaryButton onClick={pay} disabled={off} className="mt-4 w-full !py-3" data-testid="pay-by-card">{busy ? "Processing…" : `Pay ${money(summary.total, summary.currency)}`}</PrimaryButton>
      <p className="mt-2 text-xs text-gray-500">Your card is processed securely by Stripe. The licence starts only after the payment is confirmed.</p>
    </div>
  );
}

export default function LicenceCardForm(props) {
  return (
    <Elements stripe={getStripe()}>
      <CardFields {...props} />
    </Elements>
  );
}
